import { useState } from "react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Boxes } from "lucide-react";

const LOGIN_BG =
  "https://images.unsplash.com/photo-1517816743773-6e0fd518b4a6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMG1pbmltYWwlMjB3aGl0ZSUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODU4NzAyMTZ8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("michael@replenishsupply.com");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img
          src={LOGIN_BG}
          alt="Abstract architecture"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-900/10" />
        <div className="absolute bottom-12 left-12 right-12">
          <div className="flex items-center gap-2 text-white">
            <Boxes className="h-6 w-6" strokeWidth={1.5} />
            <span className="font-heading text-lg font-bold tracking-tight">
              Replenish Supply
            </span>
          </div>
          <p className="mt-3 max-w-sm text-sm text-white/80">
            Raw material catalog. Organize your categories and variants with clarity.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16 sm:px-12">
        <div className="w-full max-w-sm">
          <div className="mb-10 flex items-center gap-2 lg:hidden">
            <Boxes className="h-6 w-6 text-[#002FA7]" strokeWidth={1.5} />
            <span className="font-heading text-lg font-bold tracking-tight">
              Replenish Supply
            </span>
          </div>

          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
            Inventory Catalog
          </p>
          <h1 className="mt-2 font-heading text-4xl font-black tracking-tighter text-slate-900">
            Sign in
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            Access your raw material categories.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="rounded-sm border-slate-300 focus-visible:ring-2 focus-visible:ring-[#002FA7]"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-600">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="rounded-sm border-slate-300 focus-visible:ring-2 focus-visible:ring-[#002FA7]"
                data-testid="login-password-input"
              />
            </div>

            {error && (
              <p className="text-sm text-red-600" data-testid="login-error">
                {error}
              </p>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]"
              data-testid="login-submit-button"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
